import { useState, useEffect, useMemo } from "react";
import { newCartId, withCartIds } from "@/lib/cartId";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandLockup } from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { StockBadge, StockLegend } from "@/components/StockBadge";
import useStockStatus from "@/hooks/useStockStatus";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { liningCharts, getLiningChartUrl } from "@/data/galleryImages";
import {
  ArrowLeft,
  ZoomIn,
  X,
  Plus,
  Trash2,
  MessageCircle,
  ShoppingCart,
  Info,
} from "lucide-react";

const ADMIN_WA_NUMBER = "917624853840";
const UNIT_LABELS = { than: "Than", bundle: "Bundle" };
const UNIT_METERS = { than: 20, bundle: 24 };

export const LINING_QUALITIES = [
  { id: "600", label: "600 SD (Cambric)", productId: "600-lining" },
  { id: "600 Gold", label: "600 Gold (Premium Single Dyed)", productId: "600-gold", isNew: true },
  { id: "060", label: "060 DD (Double Dyed Cambric)", productId: "060-lining" },
  { id: "006", label: "006 DD Mercerised", productId: "006-lining" },
  { id: "6000", label: "6000 SD (Big Panna Shrinkless)", productId: "6000-shrinkless" },
  { id: "Super Queen", label: "Super Queen", productId: "super-queen" },
  { id: "303", label: "303 Pure Cotton 2x1", productId: "303-pure-cotton" },
  { id: "3000", label: "3000 Pure Cotton (Big Panna)", productId: "3000-pure-cotton" },
];

const buildWaLink = (items, customerName, notes) => {
  // Group by quality for cleaner WhatsApp formatting
  const grouped = items.reduce((acc, it) => {
    if (!acc[it.quality]) acc[it.quality] = [];
    acc[it.quality].push(it);
    return acc;
  }, {});

  const groupLines = Object.entries(grouped).flatMap(([qual, list]) => [
    "",
    `*— ${qual} —*`,
    ...list.map(
      (it, i) =>
        `${i + 1}. Shade #${it.shade} — ${it.quantity} ${UNIT_LABELS[it.unitType]}(s) (${it.meters} m)`
    ),
  ]);

  const total = items.reduce((s, it) => s + Number(it.meters || 0), 0);

  const lines = [
    "*Lining Order — Mutha Paras & Co.*",
    "",
    customerName ? `*Customer:* ${customerName}` : null,
    "",
    "*Order Details:*",
    ...groupLines,
    "",
    `*Total Lines:* ${items.length}`,
    `*Total Fabric Ordered:* ${total} m`,
    "",
    notes ? `*Notes:* ${notes}` : null,
    "",
    "_Sent via muthaparas.com — Lining catalogue_",
  ].filter(Boolean);
  return `https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function LiningCatalogue() {
  const [quality, setQuality] = useState(LINING_QUALITIES[0].label);
  const stock = useStockStatus();
  const [shadeInput, setShadeInput] = useState("");
  const [quantityInput, setQuantityInput] = useState("");
  const [unitType, setUnitType] = useState("than");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem("lining_cart");
      return saved ? withCartIds(JSON.parse(saved)) : [];
    } catch (err) {
      console.warn("Failed to restore lining cart from localStorage:", err);
      return [];
    }
  });
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem("lining_cart", JSON.stringify(items));
  }, [items]);

  const charts = useMemo(
    () => liningCharts.map((c, idx) => ({ ...c, ...getLiningChartUrl(c.file), idx })),
    []
  );

  const totalMeters = items.reduce((s, it) => s + Number(it.meters || 0), 0);

  const handleAdd = (e) => {
    e?.preventDefault();
    setError("");
    const shade = shadeInput.trim();
    const quantity = Number(quantityInput);
    if (!quality) return setError("Please choose a fabric quality.");
    if (!shade) return setError("Please enter a shade number.");
    if (!quantity || quantity <= 0) return setError("Please enter a quantity (>0).");
    const meters = quantity * UNIT_METERS[unitType];
    setItems((prev) => [...prev, { id: newCartId(), quality, shade, quantity, unitType, meters }]);
    setShadeInput("");
    setQuantityInput("");
  };

  const handleRemove = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const handleClear = () => setItems([]);

  const waLink = items.length > 0 ? buildWaLink(items, name, notes) : null;

  return (
    <div
      className="min-h-screen pb-44 sm:pb-40"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="lining-catalogue"
    >
      {/* Header */}
      <div className="border-b border-[var(--mp-line)] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm hover:opacity-70 transition"
            style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="lining-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/"
              className="text-sm font-medium hidden sm:block"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
            >
              <BrandLockup />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="mb-10 max-w-3xl">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-3"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Lining Fabric — Full Catalogue
          </p>
          <h1
            className="text-4xl md:text-5xl tracking-tight leading-none font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Pick by Quality &amp; Shade Number
          </h1>
          <p
            className="text-base leading-relaxed"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            Browse {charts.length} master shade charts covering shades 1 to 336. Tap a chart to zoom in,
            choose your fabric quality, then add the shade number and quantity to your order. Send to
            WhatsApp when ready.
          </p>
          <StockLegend className="mt-6" />
        </div>

        {/* How to order */}
        <div
          className="mb-10 p-5 border border-[var(--mp-line)] rounded-sm flex items-start gap-3 pastel-cream"
          data-testid="lining-how-to-order"
        >
          <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
          <div>
            <p className="text-sm font-medium mb-1" style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}>
              How to order
            </p>
            <ol
              className="text-xs leading-relaxed list-decimal pl-4 space-y-0.5"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              <li>Tap any chart below to zoom in and read the shade numbers.</li>
              <li>In the order panel: pick the <strong>quality</strong> (e.g. 600 SD, 303 Cotton), then enter the shade number and how many thans you need. A than is <strong>20m on small panna</strong> and <strong>24m on big panna</strong>.</li>
              <li>Click <strong>Add</strong> to keep building the list — you can mix qualities in one order.</li>
              <li>Hit <strong>Send via WhatsApp</strong> — your full order opens with details pre-filled.</li>
            </ol>
          </div>
        </div>

        {/* Charts grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-12"
          data-testid="lining-chart-grid"
        >
          {charts.map((c) => (
            <button
              key={c.file}
              onClick={() => setLightbox(c)}
              className="group relative border border-[var(--mp-line)] rounded-sm overflow-hidden cursor-zoom-in bg-white"
              data-testid={`lining-chart-${c.idx + 1}`}
            >
              <img
                src={c.thumb}
                alt={c.label}
                loading="lazy"
                className="w-full h-64 sm:h-80 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div
                className="absolute inset-0 flex items-end justify-between p-3 sm:p-4 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)" }}
              >
                <span
                  className="text-sm text-white font-medium"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {c.label}
                </span>
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
              <div
                className="absolute top-3 left-3 px-2.5 py-1 rounded-sm text-xs font-semibold"
                style={{ backgroundColor: "rgba(255,255,255,0.95)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
              >
                Chart {c.idx + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
          onClick={() => setLightbox(null)}
          data-testid="lining-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Close"
            data-testid="lining-lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.label}
            className="max-w-full max-h-[88vh] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <p
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs sm:text-sm text-white/85 px-4 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {lightbox.label} — pinch / scroll to zoom in for clear shade numbers
          </p>
        </div>
      )}

      {/* Sticky Order Pad */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--mp-line)] bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.06)]"
        data-testid="lining-order-pad"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-5">
            {/* Add row */}
            <form
              onSubmit={handleAdd}
              className="flex flex-1 flex-wrap items-end gap-2"
              data-testid="lining-add-form"
            >
              <div className="flex-1 min-w-[180px]">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Fabric Quality
                </label>
                <Select value={quality} onValueChange={setQuality}>
                  <SelectTrigger
                    className="rounded-sm border-[var(--mp-line)] py-4 text-sm"
                    data-testid="lining-quality-select"
                  >
                    <SelectValue placeholder="Choose quality" />
                  </SelectTrigger>
                  <SelectContent>
                    {LINING_QUALITIES.map((q) => (
                      <SelectItem key={q.id} value={q.label} data-testid={`lining-quality-${q.id}`}>
                        {q.label}
                        {stock[q.productId] && stock[q.productId] !== "ready"
                          ? ` · ${stock[q.productId] === "low" ? "Low stock" : "Made to order"}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-2">
                  <StockBadge
                    status={
                      stock[
                        (LINING_QUALITIES.find((q) => q.label === quality) || {}).productId
                      ] || "ready"
                    }
                  />
                </div>
              </div>
              <div className="w-24 sm:w-32">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Shade #
                </label>
                <Input
                  value={shadeInput}
                  onChange={(e) => setShadeInput(e.target.value)}
                  placeholder="e.g. 47"
                  inputMode="numeric"
                  className="rounded-sm border-[var(--mp-line)] py-4 text-sm"
                  data-testid="lining-shade-input"
                />
              </div>
              <div className="w-24 sm:w-28">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Quantity
                </label>
                <Input
                  value={quantityInput}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  placeholder="5"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  className="rounded-sm border-[var(--mp-line)] py-4 text-sm"
                  data-testid="lining-quantity-input"
                />
              </div>
              <div className="flex flex-col flex-shrink-0">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Unit
                </label>
                <div
                  className="inline-flex rounded-sm border border-[var(--mp-line)] overflow-hidden"
                  data-testid="lining-unit-toggle"
                  role="tablist"
                  aria-label="Quantity unit"
                >
                  <button
                    type="button"
                    onClick={() => setUnitType("than")}
                    className="px-3 py-3 text-xs font-medium transition"
                    style={{
                      backgroundColor: unitType === "than" ? "#101010" : "transparent",
                      color: unitType === "than" ? "var(--mp-on-solid)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="lining-unit-than"
                    aria-pressed={unitType === "than"}
                  >
                    Than <span className="opacity-70">(20m)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitType("bundle")}
                    className="px-3 py-3 text-xs font-medium border-l border-[var(--mp-line)] transition"
                    style={{
                      backgroundColor: unitType === "bundle" ? "#101010" : "transparent",
                      color: unitType === "bundle" ? "var(--mp-on-solid)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="lining-unit-bundle"
                    aria-pressed={unitType === "bundle"}
                  >
                    Bundle <span className="opacity-70">(24m)</span>
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="rounded-sm py-4 px-4 text-white text-sm flex-shrink-0"
                style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="lining-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </form>

            {/* Summary */}
            <div className="flex items-center gap-3 lg:gap-5 lg:border-l lg:border-[var(--mp-line)] lg:pl-5">
              <div className="text-right">
                <p
                  className="text-[10px] uppercase tracking-wider"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Order
                </p>
                <p
                  className="text-sm font-semibold"
                  style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="lining-total"
                >
                  {items.length} {items.length === 1 ? "shade" : "shades"} · {totalMeters} m
                </p>
              </div>
              {waLink ? (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lining-send-wa-btn"
                >
                  <Button
                    className="rounded-sm py-4 px-4 sm:px-6 text-white text-sm whitespace-nowrap whatsapp-pulse"
                    style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send via WhatsApp
                  </Button>
                </a>
              ) : (
                <Button
                  disabled
                  className="rounded-sm py-4 px-4 sm:px-6 text-white text-sm whitespace-nowrap opacity-50 cursor-not-allowed"
                  style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="lining-send-wa-btn-disabled"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send via WhatsApp
                </Button>
              )}
            </div>
          </div>

          {error && (
            <p
              className="text-xs mt-2"
              style={{ color: "#b91c1c", fontFamily: "'Manrope', sans-serif" }}
              data-testid="lining-error"
            >
              {error}
            </p>
          )}

          {items.length > 0 && (
            <div className="mt-3 border-t border-[var(--mp-line)] pt-3">
              <div
                className="flex flex-wrap gap-2 max-h-24 overflow-y-auto"
                data-testid="lining-item-list"
              >
                {items.map((it, i) => (
                  <div
                    key={it.id}
                    className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-sm border border-[var(--mp-line)] text-xs"
                    style={{ backgroundColor: "var(--mp-paper)", fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                  >
                    <span className="font-semibold" style={{ color: "var(--mp-copper)" }}>{it.quality.split("(")[0].trim()}</span>
                    <span style={{ color: "var(--mp-muted)" }}>·</span>
                    <span className="font-semibold">#{it.shade}</span>
                    <span style={{ color: "var(--mp-muted)" }}>·</span>
                    <span>{it.quantity} {UNIT_LABELS[it.unitType]}{it.quantity > 1 ? "s" : ""}</span>
                    <span style={{ color: "var(--mp-muted)" }} className="text-[10px]">({it.meters}m)</span>
                    <button
                      onClick={() => handleRemove(it.id)}
                      className="ml-1 p-0.5 hover:bg-red-50 rounded"
                      aria-label={`Remove ${it.quality} shade ${it.shade}`}
                      data-testid={`lining-remove-${i}`}
                    >
                      <X className="w-3 h-3" style={{ color: "#b91c1c" }} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleClear}
                className="text-xs mt-2 inline-flex items-center gap-1 hover:underline"
                style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="lining-clear-btn"
              >
                <Trash2 className="w-3 h-3" /> Clear all
              </button>
            </div>
          )}

          <details className="mt-3 text-xs" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
            <summary className="cursor-pointer inline-flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              Add your name &amp; notes (optional)
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name / company"
                className="rounded-sm border-[var(--mp-line)] py-3 text-sm"
                data-testid="lining-name-input"
              />
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (delivery city, special instructions)"
                className="rounded-sm border-[var(--mp-line)] py-3 text-sm"
                data-testid="lining-notes-input"
              />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
